import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, query, onSnapshot } from 'firebase/firestore';
import Sales from './Sales';
import PendingInvoices from './PendingInvoices';
import Expenses from './Expenses';
import Payments from './Payments';
import SalesReport from './reports/SalesReport';
import LedgerPaymentReport from './reports/LedgerPaymentReport';
import AgentPaymentVoucher from './reports/AgentPaymentVoucher';
import ConsolidatedReport from './reports/ConsolidatedReport';
import ManageUsers from './ManageUsers';
import CouponCode from './CouponCode';
import '../../css/accounts/AccountsDashboard.css';

const AccountsDashboard = () => {
  const [activeMenu, setActiveMenu] = useState('Dashboard');
  const [reportsOpen, setReportsOpen] = useState(false); // 🎯 State to handle Reports Submenu Toggle
  const [loading, setLoading] = useState(true);

  // Summary Metrics State
  const [metrics, setMetrics] = useState({
    totalSales: 0,
    paidAmount: 0,
    pendingAmount: 0,
    totalExpenses: 0,
    netProfit: 0,
    totalOrders: 0,
    pendingCount: 0,
    paidCount: 0,
  });

  const [recentSales, setRecentSales] = useState([]);

  // Fetch Firestore Data for Express Orders
  useEffect(() => {
    const qOrders = query(collection(db, 'expressOrder'));
    
    const unsubscribe = onSnapshot(qOrders, (snapshot) => {
      let salesSum = 0;
      let paidSum = 0;
      let pendingSum = 0;
      let pendingOrdersCount = 0;
      let paidOrdersCount = 0;

      const ordersList = snapshot.docs.map((doc) => {
        const data = doc.data();
        const payment = data.payment || {};
        const total = Number(payment.totalPayable) || 0;
        const status = (payment.status || 'Unpaid').toLowerCase();

        salesSum += total;

        if (status === 'paid') {
          paidSum += total;
          paidOrdersCount += 1;
        } else {
          pendingSum += total;
          pendingOrdersCount += 1;
        }

        return {
          id: doc.id,
          displayOrderId: data.displayOrderId || data.autoGeneratedId || doc.id,
          customer: data.userName || data.customerName || 'N/A',
          amount: total,
          status: payment.status || 'Unpaid',
          date: data.createdAt ? data.createdAt.toDate() : new Date(),
        };
      });

      // Sort recent sales by date descending
      ordersList.sort((a, b) => b.date - a.date);

      // Estimated static expenses logic for summary
      const estimatedExpenses = salesSum * 0.25; 

      setMetrics({
        totalSales: salesSum,
        paidAmount: paidSum,
        pendingAmount: pendingSum,
        totalExpenses: estimatedExpenses,
        netProfit: paidSum - estimatedExpenses,
        totalOrders: snapshot.docs.length,
        pendingCount: pendingOrdersCount,
        paidCount: paidOrdersCount,
      });

      setRecentSales(ordersList.slice(0, 5));
      setLoading(false);
    }, (error) => {
      console.error("Error fetching accounts summary: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const reportSubMenus = [
    { name: 'Sales Report', icon: '📄' },
    { name: 'Ledger Payment Report', icon: '📑' },
    { name: 'Agent Payment Report', icon: '🚚' },
    { name: 'Consolidated Report', icon: '📊' },
  ];

  const handleMenuClick = (menuName) => {
    if (menuName === 'Reports') {
      setReportsOpen(!reportsOpen);
    } else {
      setActiveMenu(menuName);
    }
  };

  return (
    <div className="accounts-layout">
      {/* Sidebar Navigation */}
      <aside className="accounts-sidebar">
        <div className="sidebar-brand">
          <h2>Mr White</h2>
          <span>Accounts Portal</span>
        </div>
        <nav className="sidebar-menu">
          
          <button
            className={`menu-item ${activeMenu === 'Dashboard' ? 'active' : ''}`}
            onClick={() => handleMenuClick('Dashboard')}
          >
            <span className="menu-icon">📊</span>
            <span className="menu-text">Dashboard</span>
          </button>

          <button
            className={`menu-item ${activeMenu === 'Sales / Invoices' ? 'active' : ''}`}
            onClick={() => handleMenuClick('Sales / Invoices')}
          >
            <span className="menu-icon">🧾</span>
            <span className="menu-text">Sales / Invoices</span>
          </button>

          <button
            className={`menu-item ${activeMenu === 'Pending Invoices' ? 'active' : ''}`}
            onClick={() => handleMenuClick('Pending Invoices')}
          >
            <span className="menu-icon">⏳</span>
            <span className="menu-text">Pending Invoices</span>
          </button>

          <button
            className={`menu-item ${activeMenu === 'Expenses' ? 'active' : ''}`}
            onClick={() => handleMenuClick('Expenses')}
          >
            <span className="menu-icon">💸</span>
            <span className="menu-text">Expenses</span>
          </button>

          <button
            className={`menu-item ${activeMenu === 'Payments' ? 'active' : ''}`}
            onClick={() => handleMenuClick('Payments')}
          >
            <span className="menu-icon">💳</span>
            <span className="menu-text">Payments</span>
          </button>

          {/* 🎯 Reports Parent Menu with Submenus */}
          <div className="menu-group">
            <button
              className={`menu-item ${activeMenu.includes('Report') ? 'active' : ''}`}
              onClick={() => handleMenuClick('Reports')}
            >
              <span className="menu-icon">📈</span>
              <span className="menu-text">Reports</span>
              <span className="menu-arrow" style={{ marginLeft: 'auto', fontSize: '10px' }}>
                {reportsOpen ? '▲' : '▼'}
              </span>
            </button>

            {reportsOpen && (
              <div className="submenu-list" style={{ paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                {reportSubMenus.map((sub) => (
                  <button
                    key={sub.name}
                    className={`menu-item submenu-item ${activeMenu === sub.name ? 'active' : ''}`}
                    onClick={() => setActiveMenu(sub.name)}
                    style={{ fontSize: '12px', padding: '8px 12px' }}
                  >
                    <span className="menu-icon">{sub.icon}</span>
                    <span className="menu-text">{sub.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className={`menu-item ${activeMenu === 'Manage Users' ? 'active' : ''}`}
            onClick={() => handleMenuClick('Manage Users')}
          >
            <span className="menu-icon">👥</span>
            <span className="menu-text">Manage Users</span>
          </button>

          {/* 🎯 Coupon Code Menu below Manage Users */}
          <button
            className={`menu-item ${activeMenu === 'Coupon Code' ? 'active' : ''}`}
            onClick={() => handleMenuClick('Coupon Code')}
          >
            <span className="menu-icon">🏷️</span>
            <span className="menu-text">Coupon Code</span>
          </button>

        </nav>
      </aside>

      {/* Main Content View (Dynamic tab rendering) */}
      <main className="accounts-main">
        {/* Dynamic Header Title */}
        <header className="dashboard-header">
          <div>
            <h1>
              {activeMenu === 'Sales / Invoices' && 'Sales & Billing Ledger'}
              {activeMenu === 'Pending Invoices' && 'Pending & Unpaid Invoices'}
              {activeMenu === 'Expenses' && 'Expense Ledgers Management'}
              {activeMenu === 'Payments' && 'Payment Voucher Entry'}
              {activeMenu === 'Manage Users' && <ManageUsers />}
              {activeMenu === 'Coupon Code' && 'Coupon Codes Management'}
              {activeMenu === 'Sales Report' && <SalesReport />}
              {activeMenu === 'Ledger Payment Report' && <LedgerPaymentReport />}
              {activeMenu === 'Agent Payment Report' && <AgentPaymentVoucher />}
              {activeMenu === 'Consolidated Report' && <ConsolidatedReport />}
              {activeMenu.includes('Report') && activeMenu}
              {activeMenu === 'Dashboard' && 'Financial Overview'}
            </h1>
            
          </div>
          
        </header>

        {/* 🎯 Tab Views */}
        {activeMenu === 'Sales / Invoices' && <Sales />}
        {activeMenu === 'Pending Invoices' && <PendingInvoices />}
        {activeMenu === 'Expenses' && <Expenses />}
        {activeMenu === 'Payments' && <Payments />}
        {activeMenu === 'Coupon Code' && <CouponCode />}

        

        {/* 🎯 Tab 2: Main Dashboard View */}
        {activeMenu === 'Dashboard' && (
          <>
            {/* Executive Summary Cards */}
            <section className="summary-grid">
              <div className="summary-card total-sales">
                <div className="card-info">
                  <span className="card-title">Gross Revenue</span>
                  <span className="card-value">AED {metrics.totalSales.toFixed(2)}</span>
                  <span className="card-sub">{metrics.totalOrders} Orders Total</span>
                </div>
                <div className="card-icon">💰</div>
              </div>

              <div className="summary-card paid-invoices">
                <div className="card-info">
                  <span className="card-title">Collected Revenue</span>
                  <span className="card-value">AED {metrics.paidAmount.toFixed(2)}</span>
                  <span className="card-sub">{metrics.paidCount} Fully Paid</span>
                </div>
                <div className="card-icon">✅</div>
              </div>

              <div className="summary-card pending-invoices">
                <div className="card-info">
                  <span className="card-title">Pending Receivables</span>
                  <span className="card-value">AED {metrics.pendingAmount.toFixed(2)}</span>
                  <span className="card-sub">{metrics.pendingCount} Unpaid Orders</span>
                </div>
                <div className="card-icon">⏳</div>
              </div>

              <div className="summary-card net-profit">
                <div className="card-info">
                  <span className="card-title">Net Operating Profit</span>
                  <span className="card-value">AED {metrics.netProfit.toFixed(2)}</span>
                  <span className="card-sub">After Est. Expenses</span>
                </div>
                <div className="card-icon">📈</div>
              </div>
            </section>

            {/* Operational Analytics & Breakdown */}
            <section className="dashboard-grid">
              {/* Recent Invoices Table */}
              <div className="dashboard-card recent-sales-card">
                <div className="card-header">
                  <h3>Recent Sales Transactions</h3>
                  <span 
                    className="view-all-link"
                    style={{ cursor: 'pointer', color: '#2563eb', fontWeight: '600' }}
                    onClick={() => setActiveMenu('Sales / Invoices')}
                  >
                    View All →
                  </span>
                </div>
                <div className="table-responsive">
                  <table className="dashboard-table">
                    <thead>
                      <tr>
                        <th>Invoice ID</th>
                        <th>Customer</th>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan="5" className="table-loader">Loading billing records...</td></tr>
                      ) : recentSales.length === 0 ? (
                        <tr><td colSpan="5" className="table-loader">No recent orders found.</td></tr>
                      ) : (
                        recentSales.map((order) => (
                          <tr key={order.id}>
                            <td className="inv-code">{order.displayOrderId}</td>
                            <td className="cust-name">{order.customer}</td>
                            <td>{order.date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</td>
                            <td className="amt-code">AED {order.amount.toFixed(2)}</td>
                            <td>
                              <span className={`status-tag ${order.status.toLowerCase().replace(/\s+/g, '-')}`}>
                                {order.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Accounts Breakdown Panel */}
              <div className="dashboard-card analytics-card">
                <h3>Revenue Collection Ratio</h3>
                <div className="progress-container">
                  <div className="progress-info">
                    <span>Paid Realization</span>
                    <span>
                      {metrics.totalSales > 0 ? ((metrics.paidAmount / metrics.totalSales) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill paid-fill" 
                      style={{ width: `${metrics.totalSales > 0 ? (metrics.paidAmount / metrics.totalSales) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="progress-container">
                  <div className="progress-info">
                    <span>Pending Outstanding</span>
                    <span>
                      {metrics.totalSales > 0 ? ((metrics.pendingAmount / metrics.totalSales) * 100).toFixed(1) : 0}%
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill pending-fill" 
                      style={{ width: `${metrics.totalSales > 0 ? (metrics.pendingAmount / metrics.totalSales) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                <div className="quick-stats-list">
                  <div className="stat-item">
                    <span>Est. Expenses:</span>
                    <strong>AED {metrics.totalExpenses.toFixed(2)}</strong>
                  </div>
                  <div className="stat-item">
                    <span>Average Ticket Value:</span>
                    <strong>
                      AED {metrics.totalOrders > 0 ? (metrics.totalSales / metrics.totalOrders).toFixed(2) : '0.00'}
                    </strong>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {/* Placeholder Views for Other Unconfigured Menu Tabs */}
        
      </main>
    </div>
  );
};

export default AccountsDashboard;